import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { db, storage } from "@lib/firebase";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
  limit,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import { jsPDF } from "jspdf";
import { InputFieldRace, VoucherInput, Button } from "@mmfestival/shared-components";

// NOTE: Add this to public/index.html if you plan to include PayPal SDK statically:
// <script src="https://www.paypal.com/sdk/js?client-id=YOUR_CLIENT_ID&currency=USD&intent=capture"></script>

const DEFAULT_IDR_TO_USD_RATE = 17000; // Fallback if API fails


/**
 * FormPendaftaran
 *
 * Changes in this version:
 * - UI and notifications remain English (as requested).
 * - Declaration PDF language now follows participant country:
 *   - If country is Indonesia (case-insensitive match) -> Indonesian ("id")
 *   - Otherwise -> English ("en")
 * - Validation remains relaxed as previously adjusted.
 *
 * Behavior:
 * - When generating/saving the declaration PDF we detect the country from formData.negara
 *   and generate the PDF in Indonesian for Indonesia, otherwise in English.
 * - When archiving registration documents, do NOT delete as a dangerous fallback.
 *   If batch archive fails, mark the original document with archival metadata (archiveStatus, archiveError, archiveRetryRequestedAt, archiveRetryCount)
 *   so it remains available for manual inspection or server-side retry.
 */

export const FormPendaftaran = ({ labelColor = "text-slate-800" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { title, price, foreignerPrice, eventId } = location.state || {};

  // The category is passed through router state, so a direct visit or a page
  // refresh arrives without it. Send the user back to that event's own page
  // (/SMM, /JMM, /BMM) to pick a category - there is no /kegiatan route here.
  const eventHomePath = slug ? `/${slug.toUpperCase()}` : "/";
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const paypalRef = useRef(null);

  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    namaLengkap: "",
    email: "",
    telepon: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    gender: "",
    negara: "Indonesia",
    provinsi: "",
    kota: "",
    alamat: "",
    kontakDarurat: "",
    namaKontakDarurat: "",
    hubunganKontakDarurat: "",
    // ensure these profile-related fields exist so they can be autofilled and saved back to profile
    bibName: "",
    golonganDarah: "",
    sizeJersey: "",
    // Parent consent file for under-18 participants in 10K category
    parentConsentFile: null,
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const isSnapOpenRef = useRef(false);
  const [registrationStatus, setRegistrationStatus] = useState(null); // "registered" | "not_registered" | null
  const [transactionStatus, setTransactionStatus] = useState(null); // e.g. "pending", "settlement"
  const [snapToken, setSnapToken] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [voucherCode, setVoucherCode] = useState(null);
  const [discount, setDiscount] = useState(0);

  // Payment method: "midtrans" | "paypal"
  const [paymentMethod, setPaymentMethod] = useState("midtrans");

  // localPrice/localForeignerPrice will be sourced from either location.state (if provided)
  // or from the selected category fetched from Firestore
  const [localPrice, setLocalPrice] = useState(
    typeof price === "number" ? price : 0
  );
  const [localForeignerPrice, setLocalForeignerPrice] = useState(
    foreignerPrice !== undefined && foreignerPrice !== null
      ? foreignerPrice
      : null
  );

  const [finalPrice, setFinalPrice] = useState(
    typeof price === "number" ? price : 0
  );
  const [collectionName, setCollectionName] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [distance, setDistance] = useState(null);

  // New: store event display name for the sidebar
  const [eventName, setEventName] = useState(null);

  // New: store event date for age validation (default to JMM date if eventId is JMM)
  const [eventDate, setEventDate] = useState(
    eventId === "JMM" ? new Date("2026-04-25") : new Date()
  );
  const [idrToUsdRate, setIdrToUsdRate] = useState(DEFAULT_IDR_TO_USD_RATE);

  // default language for generated declaration from the form (fallback only)
  const [declarationLang] = useState("id");

  // Helper to determine declaration language according to country
  const declarationLangForCountry = (negara) => {
    const norm = (negara || "").toString().trim().toLowerCase();
    return norm && norm === "indonesia" ? "id" : "en";
  };

  // === MAPPING EVENT DETAILS FOR DECLARATION PDF ===
  const getEventDetails = (eventId) => {
    const eventDetailsMap = {
      JMM: {
        namaEvent: "Java Mountain Marathon (JMM)",
        lokasi: "West Java",
        tahun: "2026",
        tipeKegiatan: "trail_running",
      },
      SMM: {
        namaEvent: "Sembalun Mountain Marathon (SMM)",
        lokasi: "Lombok, West Nusa Tenggara",
        tahun: "2026",
        tipeKegiatan: "trail_running",
      },
      BMM: {
        namaEvent: "Bali Mountain Marathon (BMM)",
        lokasi: "Bali",
        tahun: "2026",
        tipeKegiatan: "trail_running",
      },
    };
    return eventDetailsMap[eventId] || eventDetailsMap["JMM"];
  };

  // === DYNAMIC PRICING ===
  useEffect(() => {
    const negaraNormalized = (formData.negara || "")
      .toString()
      .trim()
      .toLowerCase();
    const isForeigner = negaraNormalized !== "indonesia" && negaraNormalized;

    let basePrice = typeof localPrice === "number" ? localPrice : 0;

    if (typeof localForeignerPrice === "number" && isForeigner) {
      basePrice = localForeignerPrice;
    }

    const newFinalPrice = Math.floor(basePrice * (1 - (discount || 0) / 100));
    setFinalPrice(newFinalPrice);
  }, [formData.negara, localPrice, localForeignerPrice, discount]);

  // ---------------- Dynamic PayPal SDK loader ----------------
  const loadPayPalSdk = (clientId, currency = "USD") => {
    return new Promise((resolve, reject) => {
      try {
        if (!clientId) {
          return reject(new Error("PayPal client id not provided"));
        }

        if (window.paypal) {
          return resolve(window.paypal);
        }

        const existing = document.querySelector(
          'script[data-paypal-sdk="true"]'
        );
        if (existing) {
          existing.addEventListener("load", () => {
            if (window.paypal) resolve(window.paypal);
            else
              reject(new Error("PayPal SDK loaded but window.paypal missing"));
          });
          existing.addEventListener("error", () =>
            reject(new Error("Failed to load PayPal SDK"))
          );
          return;
        }

        const script = document.createElement("script");
        script.setAttribute("data-paypal-sdk", "true");
        script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}&intent=capture`;
        script.async = true;
        script.onload = () => {
          if (window.paypal) {
            resolve(window.paypal);
          } else {
            reject(
              new Error("PayPal SDK loaded but window.paypal not available")
            );
          }
        };
        script.onerror = (e) => {
          reject(new Error("Failed to load PayPal SDK"));
        };
        document.head.appendChild(script);
      } catch (err) {
        reject(err);
      }
    });
  };

  // Preload PayPal SDK and fetch dynamic exchange rate
  useEffect(() => {
    if (paymentMethod !== "paypal") return;

    // 1. Load SDK
    const CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID;
    loadPayPalSdk(CLIENT_ID).catch((err) => {
      showNotification(
        "error",
        "Failed to load PayPal SDK. Check console for details."
      );
    });

    // 2. Fetch fresh exchange rate from backend
    const fetchRate = async () => {
      try {
        const resp = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/payment/paypal/currency-rate`);
        if (resp.data?.rate && typeof resp.data.rate === "number") {
          setIdrToUsdRate(resp.data.rate);
        }
      } catch (err) {
        console.warn("[FormPendaftaran] Failed to fetch dynamic rate, using fallback:", err.message);
      }
    };
    fetchRate();
  }, [paymentMethod, showNotification]);

  // === PAYPAL BUTTON RENDER (always English messages) ===
  useEffect(() => {
    const renderPayPalButtons = async () => {
      if (!orderId || paymentMethod !== "paypal" || finalPrice === 0) {
        return;
      }

      const CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID;
      try {
        await loadPayPalSdk(CLIENT_ID, "USD");
      } catch (err) {
        showNotification(
          "error",
          "Failed to load PayPal. Check console/network."
        );
        return;
      }

      if (!window.paypal) {
        showNotification("error", "PayPal SDK not available.");
        return;
      }

      if (paypalRef.current) paypalRef.current.innerHTML = "";

      try {
        window.paypal
          .Buttons({
            createOrder: async () => {
              // Reverse-Fee Model Calculation (v2)
              const FEE_RATE = 0.039;
              const FIXED_FEE = 0.30;
              const SPREAD = 0.035;

              const withdrawalRate = idrToUsdRate * (1 - SPREAD);
              const netNeeded = finalPrice / withdrawalRate;
              const amountUsd = ((netNeeded + FIXED_FEE) / (1 - FEE_RATE)).toFixed(2);
              try {
                const resp = await axios.post(
                  `${
                    import.meta.env.VITE_API_BASE_URL
                  }/api/paypal/create-order`,
                  {
                    amount: amountUsd,
                    currency: "USD",
                    registrationId: orderId,
                  }
                );
                if (!resp.data || !resp.data.id) {
                  throw new Error("PayPal create-order did not return id");
                }
                return resp.data.id;
              } catch (err) {
                showNotification(
                  "error",
                  "Failed to create PayPal order. Check server/console."
                );
                throw err;
              }
            },
            onApprove: async (data) => {
              setLoading(true);
              try {
                await axios.post(
                  `${
                    import.meta.env.VITE_API_BASE_URL
                  }/api/paypal/capture-order`,
                  {
                    paypalOrderId: data.orderID,
                    registrationId: orderId,
                  }
                );
                showNotification("success", "Payment successful via PayPal!");
                navigate(
                  `/MyOrders?order_id=${orderId}&transaction_status=settlement`
                );
              } catch (err) {
                showNotification(
                  "error",
                  "PayPal capture failed. Check server logs."
                );
              } finally {
                setLoading(false);
              }
            },
            onError: (err) => {
              showNotification(
                "error",
                "PayPal error occurred. Check console."
              );
            },
            onCancel: (data) => {
              showNotification("warning", "Payment cancelled");
            },
          })
          .render(paypalRef.current);
      } catch (err) {
        showNotification(
          "error",
          "Failed to render PayPal buttons. Check console."
        );
      }
    };

    renderPayPalButtons();

    return () => {
      if (paypalRef.current) paypalRef.current.innerHTML = "";
    };
  }, [orderId, paymentMethod, finalPrice, navigate, showNotification]);

  // === HELPER FUNCTIONS ===
  const humanize = (str) => {
    if (!str) return "";
    const map = {
      namaDepan: "First Name",
      namaBelakang: "Last Name",
      namaLengkap: "Full Name",
      email: "Email",
      telepon: "Phone",
      nomorIdentitas: "Identification Number",
      tanggalLahir: "Date of Birth",
      gender: "Gender",
      negara: "Country",
      provinsi: "Province",
      kota: "City",
      alamat: "Address",
      kontakDarurat: "Emergency Contact",
      namaKontakDarurat: "Emergency Contact Name",
      hubunganKontakDarurat: "Emergency Contact Relationship",
      bibName: "BIB Name",
      golonganDarah: "Blood Type",
      sizeJersey: "Jersey Size",
    };
    if (map[str]) return map[str];
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const fetchCollectionName = async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventRef);

      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const collName = eventData.collectionName;

        if (!collName) {
          throw new Error("collectionName not found");
        }

        setCollectionName(collName);

        // set an event display name for the sidebar.
        const displayName =
          eventData.title ||
          eventData.name ||
          eventData.namaEvent ||
          getEventDetails(eventId).namaEvent ||
          eventId;
        setEventName(displayName);

        return eventData;
      }

      throw new Error("Event not found in events collection");
    } catch (error) {
      setErrorMsg("Failed to fetch event data. Please try again.");
      showNotification(
        "error",
        "Failed to fetch event data. Please try again."
      );
      return null;
    }
  };

  const fetchCategoryDetails = async (eventId, title) => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        if (eventData.type === "seminar") {
          setCategoryName(eventData.title);
          setDistance(0);
        } else {
          const categories = eventData.categories || [];
          let selectedCategory = categories.find((cat) =>
            title.includes(cat.categoryName)
          );
          if (!selectedCategory && categories.length > 0) {
            selectedCategory = categories[0];
          }
          if (selectedCategory) {
            setCategoryName(selectedCategory.categoryName);
            setDistance(selectedCategory.distance || 0);

            // IMPORTANT: set local prices from Firestore category
            if (typeof selectedCategory.price === "number") {
              setLocalPrice(selectedCategory.price);
            } else {
              setLocalPrice(0);
            }

            if (
              selectedCategory.foreignerPrice !== undefined &&
              selectedCategory.foreignerPrice !== null
            ) {
              setLocalForeignerPrice(selectedCategory.foreignerPrice);
            } else {
              setLocalForeignerPrice(
                foreignerPrice !== undefined && foreignerPrice !== null
                  ? foreignerPrice
                  : null
              );
            }

            // SET EVENT DATE for age validation
            if (selectedCategory.date) {
              setEventDate(new Date(selectedCategory.date));
            } else if (selectedCategory.eventDateTimestamp) {
              // try parse from timestamp string e.g. "27 Sept 2025 at 05:30:00 UTC+7"
              const datePart = selectedCategory.eventDateTimestamp.split(" at ")[0];
              if (datePart) {
                const parsed = new Date(datePart);
                if (!isNaN(parsed.getTime())) {
                  setEventDate(parsed);
                }
              }
            }
          } else {
            throw new Error("Category not found in event data.");
          }
        }
      } else {
        throw new Error("Event document not found.");
      }
    } catch (error) {
      setErrorMsg("Failed to load category details. Please try again.");
      showNotification(
        "error",
        "Failed to load category details. Please try again."
      );
    }
  };

  /**
   * Archive a registration document into archivedRegistrations using a batch.
   * If the batch fails, DO NOT delete the original. Instead mark it for archival retry
   * (archiveStatus/archiveError/archiveRetryRequestedAt/archiveRetryCount).
   * This prevents irreversible data loss when client-side archiving fails.
   */
  const archiveRegistration = async (
    docRef,
    docData,
    originalCollectionName
  ) => {
    if (!docRef) return;
    try {
      const batch = writeBatch(db);
      const archivedRef = doc(db, "archivedRegistrations", docRef.id);

      const payload = {
        ...docData,
        originalCollection: originalCollectionName || null,
        originalDocRefPath: docRef.path,
        archivedAt: new Date(),
        archivedByUid: user?.uid || null,
      };

      batch.set(archivedRef, payload);
      batch.delete(docRef);

      await batch.commit();
    } catch (e) {
      // Archive failed - expected since collections are backend-only
      // Log as warning (not blocking for user, backend will handle cleanup)
      console.warn("[Archive] Client-side archive blocked:", e.code);
    }
  };

  /**
   * Improved checkRegistration:
   * - Query by uid + eventId (limit 1).
   * - Treat as registered only if transaction_status is clearly 'pending' or 'settlement'.
   * - If transaction_status is ambiguous (null/undefined/unknown) but snapToken/orderId exists:
   *     try to verify status with backend, update Firestore if pending/settlement,
   *     otherwise move the document to archivedRegistrations so user may retry.
   * - If status is expired/cancel/deny/failed -> move doc to archivedRegistrations and treat as not_registered.
   */
  const checkRegistration = async (collectionNameToCheck) => {
    if (!user || !eventId || !collectionNameToCheck) {
      return;
    }

    try {
      const q = query(
        collection(db, collectionNameToCheck),
        where("uid", "==", user.uid),
        where("eventId", "==", eventId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setRegistrationStatus("not_registered");
        setTransactionStatus(null);
        setSnapToken(null);
        setOrderId(null);
        setVoucherCode(null);
        return;
      }

      const getStatusPriority = (statusValue) => {
        if (statusValue === "settlement") return 4;
        if (statusValue === "pending") return 3;
        if (!statusValue) return 2;
        if (["expire", "cancel", "deny", "failed"].includes(statusValue)) {
          return 1;
        }
        return 0;
      };

      const getComparableTimestamp = (value) => {
        if (!value) return 0;
        if (typeof value?.toDate === "function") {
          return value.toDate().getTime();
        }
        if (typeof value?.seconds === "number") {
          return value.seconds * 1000;
        }
        if (typeof value === "string") {
          const parsed = new Date(value).getTime();
          return Number.isNaN(parsed) ? 0 : parsed;
        }
        if (value instanceof Date) {
          return value.getTime();
        }
        return 0;
      };

      const docSnap = querySnapshot.docs
        .slice()
        .sort((a, b) => {
          const aData = a.data() || {};
          const bData = b.data() || {};
          const priorityDiff =
            getStatusPriority(bData.transaction_status || null) -
            getStatusPriority(aData.transaction_status || null);

          if (priorityDiff !== 0) return priorityDiff;

          const timeA = Math.max(
            getComparableTimestamp(aData.updatedAt),
            getComparableTimestamp(aData.createdAt),
            getComparableTimestamp(aData.transaction_time)
          );
          const timeB = Math.max(
            getComparableTimestamp(bData.updatedAt),
            getComparableTimestamp(bData.createdAt),
            getComparableTimestamp(bData.transaction_time)
          );

          return timeB - timeA;
        })[0];
      const data = docSnap.data() || {};

      // Normalize fields (support legacy names)
      const statusRaw = data.transaction_status;
      const status = typeof statusRaw === "string" ? statusRaw : null;
      const paymentType = data.payment_type || data.paymentMethod || null;
      const harga = data.harga || 0;
      const docSnapToken = data.snapToken || null;
      const docOrderId = data.orderId || data.order_id || null;

      // Clearly settled -> registered
      if (status === "settlement") {
        setRegistrationStatus("registered");
        setTransactionStatus("settlement");
        setSnapToken(docSnapToken);
        setOrderId(docOrderId);
        setVoucherCode(data.voucherCode || null);
        return;
      }

      // Clearly pending -> registered (resume payment)
      if (status === "pending") {
        setRegistrationStatus("registered");
        setTransactionStatus("pending");
        setSnapToken(docSnapToken);
        setOrderId(docOrderId);
        setVoucherCode(data.voucherCode || null);
        return;
      }

      // Expired / cancelled / denied / failed -> allow re-register
      // Note: Archiving disabled client-side (backend-only via Cloud Functions)
      if (["expire", "cancel", "deny", "failed"].includes(status)) {
        // Backend cleanup job will handle archiving expired registrations
        console.log("[CheckReg] Expired/failed registration found, allowing re-register");
        setRegistrationStatus("not_registered");
        setTransactionStatus(null);
        setSnapToken(null);
        setOrderId(null);
        setVoucherCode(null);
        return;
      }

      // Ambiguous status (null / unknown)
      if (!status) {
        // If there's an orderId or snapToken, attempt to verify with backend
        if (docOrderId || docSnapToken) {
          try {
            const resp = await axios.get(
              `${
                import.meta.env.VITE_API_BASE_URL
              }/api/payment/status/${docOrderId}`,
              { params: { eventId }, timeout: 5000 }
            );

            const updatedStatus =
              resp?.data?.transaction_status || resp?.data?.status || null;

            if (updatedStatus === "pending" || updatedStatus === "settlement") {
              try {
                await updateDoc(docSnap.ref, {
                  transaction_status: updatedStatus,
                  transaction_checked_at: new Date(),
                });
              } catch (e) {
                // ignore update failure
              }

              setRegistrationStatus("registered");
              setTransactionStatus(updatedStatus);
              setSnapToken(docSnapToken);
              setOrderId(docOrderId);
              setVoucherCode(data.voucherCode || null);
              return;
            } else {
              // Backend will handle archiving
              console.log("[CheckReg] Invalid status from backend, allowing re-register");
              setRegistrationStatus("not_registered");
              setTransactionStatus(null);
              setSnapToken(null);
              setOrderId(null);
              setVoucherCode(null);
              return;
            }
          } catch (err) {
            // Backend will handle archiving
            console.log("[CheckReg] Backend verification failed, allowing re-register");
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            setOrderId(null);
            setVoucherCode(null);
            return;
          }
        } else {
          // Backend will handle archiving
          console.log("[CheckReg] No orderId/snapToken, allowing re-register");
          setRegistrationStatus("not_registered");
          setTransactionStatus(null);
          setSnapToken(null);
          setOrderId(null);
          setVoucherCode(null);
          return;
        }
      }

      setRegistrationStatus("not_registered");
      setTransactionStatus(null);
      setSnapToken(null);
      setOrderId(null);
      setVoucherCode(null);
    } catch (err) {
      setErrorMsg("Failed to check registration status.");
      showNotification("error", "Failed to check registration status.");
      setRegistrationStatus("not_registered");
      setTransactionStatus(null);
      setSnapToken(null);
      setOrderId(null);
      setVoucherCode(null);
    }
  };

  // === INITIALIZATION EFFECT ===
  useEffect(() => {
    if (!eventId || !title) {
      showNotification(
        "error",
        "Please select a category from the event page."
      );
      navigate(eventHomePath);
      return;
    }

    const initialize = async () => {
      if (!user) {
        navigate("/login");
        showNotification("error", "Please log in to register.");
        return;
      }

      try {
        const eventData = await fetchCollectionName(eventId);
        if (!eventData) {
          navigate(eventHomePath);
          return;
        }

        await fetchCategoryDetails(eventId, title);

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          let namaDepan = userData.namaDepan || "";
          let namaBelakang = userData.namaBelakang || "";
          let namaLengkap = userData.namaLengkap || "";

          if (!namaDepan && !namaBelakang && namaLengkap) {
            const namaSplit = namaLengkap.trim().split(/\s+/);
            namaDepan = namaSplit[0] || "";
            namaBelakang = namaSplit.slice(1).join(" ") || "";
          }

          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap: namaLengkap || `${namaDepan} ${namaBelakang}`.trim(),
            email: userData.email || user.email || "",
            telepon: userData.telepon || "",
            nomorIdentitas: userData.nomorIdentitas || "",
            tanggalLahir: userData.tanggalLahir || "",
            gender: userData.gender || "",
            negara: userData.negara || "Indonesia",
            provinsi: userData.provinsi || "",
            kota: userData.kota || "",
            alamat: userData.alamat || "",
            kontakDarurat: userData.kontakDarurat || "",
            namaKontakDarurat: userData.namaKontakDarurat || "",
            hubunganKontakDarurat: userData.hubunganKontakDarurat || "",
            // populate profile fields if present
            bibName: userData.bibName || "",
            golonganDarah: userData.golonganDarah || "",
            sizeJersey: userData.sizeJersey || "",
          }));
        } else {
          const namaSplit = (user.displayName || "").trim().split(/\s+/);
          const namaDepan = namaSplit[0] || "";
          const namaBelakang = namaSplit.slice(1).join(" ") || "";

          setFormData((prev) => ({
            ...prev,
            namaDepan,
            namaBelakang,
            namaLengkap:
              namaDepan && namaBelakang
                ? `${namaDepan} ${namaBelakang}`.trim()
                : "",
            email: user.email || "",
          }));
        }
      } catch (err) {
        setErrorMsg("Failed to fetch user data.");
        showNotification("error", "Failed to fetch user data.");
      }
    };

    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);

    if (eventId && title && user) {
      initialize();
    }

    return () => clearInterval(checkSnap);
  }, [eventId, title, price, foreignerPrice, navigate, showNotification, user]);

  // === CHECK REGISTRATION WHEN COLLECTION NAME IS AVAILABLE ===
  useEffect(() => {
    if (collectionName) {
      checkRegistration(collectionName);
    }
  }, [collectionName]);

  // === EVENT HANDLERS ===
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "namaDepan" || name === "namaBelakang") {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        updated.namaLengkap = `${updated.namaDepan || ""} ${
          updated.namaBelakang || ""
        }`.trim();
        return updated;
      });
    } else if (name === "namaLengkap") {
      const namaSplit = value.trim().split(/\s+/);
      setFormData((prev) => ({
        ...prev,
        namaLengkap: value,
        namaDepan: namaSplit[0] || "",
        namaBelakang: namaSplit.slice(1).join(" ") || "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleApplyVoucher = (code, discountPercentage) => {
    setVoucherCode(code);
    setDiscount(discountPercentage);
  };

  const updateUserData = async (updatedFields) => {
    try {
      if (!user || !user.uid) return;

      const docRef = doc(db, "users", user.uid);
      const fieldsToUpdate = {};
      Object.keys(updatedFields).forEach((key) => {
        const val = updatedFields[key];
        if (
          val !== undefined &&
          !(typeof val === "string" && val.trim() === "")
        ) {
          fieldsToUpdate[key] = val;
        }
      });

      if (Object.prototype.hasOwnProperty.call(fieldsToUpdate, "role")) {
        delete fieldsToUpdate.role;
      }

      if (Object.keys(fieldsToUpdate).length === 0) {
        return;
      }

      fieldsToUpdate.updatedAt = new Date();

      console.log("[DEBUG] About to update user document:", {
        userId: user.uid,
        fieldsToUpdate: Object.keys(fieldsToUpdate),
        hasRole: 'role' in fieldsToUpdate,
        hasIsAdmin: 'isAdmin' in fieldsToUpdate
      });

      await setDoc(docRef, fieldsToUpdate, { merge: true });
      
      console.log("[DEBUG] User document updated successfully");
    } catch (err) {
      console.error("[ERROR] Failed to update user data:", {
        error: err,
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      setErrorMsg("Failed to update user data.");
      showNotification("error", "Failed to update user data.");
      throw err;
    }
  };

  // === SAVE DECLARATION (PDF) ===
  const buildDeclarationContent = (
    lang,
    eventDetails,
    namaLengkap,
    tanggalStr
  ) => {
    // returns { title, nameLabel, dateLabel, paragraphs }
    if (lang === "en") {
      const title = "DECLARATION LETTER";
      const nameLabel = "Name";
      const dateLabel = "Date";
      const paragraphs = [
        `I hereby declare that I voluntarily and knowingly register myself as a participant in ${eventDetails.namaEvent} ${eventDetails.tahun}, organized by Mountain Marathon Festival (MMF), which will be held in ${eventDetails.lokasi}.`,
        `I understand that this event is physically and mentally demanding and involves potential risks such as extreme weather, difficult terrain, altitude, and other health hazards. Therefore, I confirm that I have adequately prepared myself and am in good health to participate.`,
        `I acknowledge that the event organizers, volunteers, sponsors, and other related parties cannot be held legally responsible for injuries, accidents, loss, or other emergencies that may occur during my participation. I accept full responsibility for my personal safety and health during the event and release the organizers from any legal claims resulting from or related to my participation.`,
        "I commit to:",
        [
          "Following all rules and instructions from organizers and authorities.",
          "Maintaining sportsmanship, safety, and order throughout the event.",
          "Not engaging in actions that harm myself, other participants, organizers, or the environment.",
        ],
        `I grant Mountain Marathon Festival (MMF) full permission to use any documentation (photos, videos, or other media) that include my image or identity for publication, promotion, and documentation purposes without requiring further consent.`,
        `By signing this declaration, I confirm I have read, understood, and agree to all terms voluntarily and without coercion. This declaration represents my commitment and responsibility as a participant in ${eventDetails.namaEvent}.`,
      ];
      return { title, nameLabel, dateLabel, paragraphs };
    } else {
      // Indonesian version
      const title = "SURAT PERNYATAAN";
      const nameLabel = "Nama";
      const dateLabel = "Tanggal";
      const paragraphs = [
        `Dengan ini saya menyatakan bahwa saya secara sukarela dan sadar mendaftarkan diri sebagai peserta dalam kegiatan ${eventDetails.namaEvent} tahun ${eventDetails.tahun} yang diselenggarakan oleh Mountain Marathon Festival (MMF), yang akan dilaksanakan di ${eventDetails.lokasi}.`,
        `Saya memahami bahwa kegiatan ini menantang secara fisik dan mental, serta memiliki potensi risiko, seperti kondisi cuaca ekstrem, kontur medan yang berat, ketinggian, dan gangguan kesehatan lainnya. Oleh karena itu, saya menyatakan bahwa saya telah mempersiapkan diri secara optimal untuk mengikuti kegiatan ini dan dalam kondisi kesehatan yang baik.`,
        `Saya juga menyadari bahwa panitia penyelenggara, relawan, sponsor, maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian darurat lainnya yang mungkin saya alami selama mengikuti rangkaian kegiatan. Seluruh tanggung jawab atas keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung jawab saya sendiri, dan saya melepaskan panitia dari segala bentuk tuntutan hukum atas kejadian yang mungkin timbul selama atau akibat keikutsertaan saya dalam kegiatan ini.`,
        `Saya berkomitmen untuk:`,
        [
          "Mengikuti seluruh peraturan dan arahan dari panitia dan pihak berwenang.",
          "Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan berlangsung.",
          "Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain, panitia, maupun lingkungan sekitar.",
        ],
        `Saya juga memberikan izin penuh kepada panitia Mountain Marathon Festival (MMF) untuk menggunakan dokumentasi (foto, video, atau lainnya) yang memuat identitas atau citra saya untuk keperluan publikasi, promosi, dan dokumentasi kegiatan tanpa memerlukan izin tambahan.`,
        `Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan dari pihak mana pun. Pernyataan ini saya buat sebagai bentuk komitmen dan tanggung jawab saya sebagai peserta dalam ${eventDetails.namaEvent}.`,
      ];
      return { title, nameLabel, dateLabel, paragraphs };
    }
  };

  const saveDeclarationAsPDF = async (lang) => {
    // Determine lang fallback if not provided
    const langToUse = lang || declarationLang;
    const eventDetails = getEventDetails(eventId);
    const namaLengkap = formData.namaLengkap || "Unknown";
    const tanggal = new Date().toLocaleDateString(
      langToUse === "en" ? "en-US" : "id-ID",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }
    );

    const content = buildDeclarationContent(
      langToUse,
      eventDetails,
      namaLengkap,
      tanggal
    );

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 20;
    const marginTop = 20;
    const lineHeight = 7;
    const bulletIndent = 5;
    let yPosition = marginTop;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(content.title, 105, yPosition, { align: "center" });
    yPosition += lineHeight * 2;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");

    pdf.text(`${content.nameLabel}: ${namaLengkap}`, marginLeft, yPosition);
    yPosition += lineHeight;
    pdf.text(`${content.dateLabel}: ${tanggal}`, marginLeft, yPosition);
    yPosition += lineHeight * 2;

    for (const item of content.paragraphs) {
      if (Array.isArray(item)) {
        for (const bullet of item) {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(`• ${bullet}`, marginLeft + bulletIndent, yPosition);
          yPosition += lineHeight;
        }
      } else {
        const lines = pdf.splitTextToSize(item, 170);
        for (const line of lines) {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        }
      }
      yPosition += lineHeight;
    }

    if (yPosition + lineHeight * 3 > 277) {
      pdf.addPage();
      yPosition = marginTop;
    }

    pdf.text(
      langToUse === "en" ? "Sincerely," : "Hormat saya,",
      170,
      yPosition,
      {
        align: "right",
      }
    );
    yPosition += lineHeight * 2;
    pdf.setFont("helvetica", "bold");
    pdf.text(namaLengkap, 170, yPosition, { align: "right" });

    const pdfBlob = pdf.output("blob");
    const fileName = `Declaration_${eventDetails.namaEvent.replace(
      /\s+/g,
      "_"
    )}_${namaLengkap.replace(/\s+/g, "_")}_${eventId}_${eventDetails.tahun}_${langToUse}.pdf`;

    try {
      // Check existing document for this user/event/lang
      const suratQuery = query(
        collection(db, "suratPernyataan"),
        where("userId", "==", user.uid),
        where("eventId", "==", eventId),
        where("tahun", "==", eventDetails.tahun),
        where("lang", "==", langToUse),
        limit(1)
      );
      const suratSnapshot = await getDocs(suratQuery);
      if (!suratSnapshot.empty) {
        // already exists - skip upload
        return;
      }

      const storageRef = ref(
        storage,
        `suratPernyataan/${user.uid}/${fileName}`
      );
      await uploadBytes(storageRef, pdfBlob);
      const downloadURL = await getDownloadURL(storageRef);

      await addDoc(collection(db, "suratPernyataan"), {
        namaLengkap,
        tanggal,
        fileName,
        downloadURL,
        createdAt: new Date(),
        userId: user.uid,
        eventId,
        tahun: eventDetails.tahun,
        lang: langToUse,
      });
    } catch (err) {
      setErrorMsg("Failed to save declaration.");
      showNotification("error", "Failed to save declaration.");
      throw err;
    }
  };

  // === VALIDATION (always English messages, relaxed rules) ===
  const validateFormat = (field, value) => {
    const label = humanize(field);

    if (!value || (typeof value === "string" && value.trim() === "")) {
      return `${label} is required`;
    }

    switch (field) {
      case "namaDepan":
      case "namaBelakang": {
        const trimmed = String(value).trim();
        if (trimmed.length > 50 || !/^[\p{L}\s'.-]{1,50}$/u.test(trimmed)) {
          return `${label} may only contain letters, spaces or common name punctuation (max 50 chars).`;
        }
        break;
      }

      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return "Email must be a valid email address.";
        }
        break;

      case "telepon":
      case "kontakDarurat": {
        const digits = String(value).replace(/\D/g, "");
        if (digits.length < 6 || digits.length > 15) {
          return `${label} must contain 6-15 digits.`;
        }
        break;
      }

      case "nomorIdentitas": {
        const val = String(value).trim();
        if (!/^\d{16}$/.test(val) && !/^[A-Za-z0-9]{5,20}$/.test(val)) {
          return "Identification Number must be 16 digits or 5-20 alphanumeric characters.";
        }
        break;
      }

      case "tanggalLahir": {
        const date = new Date(value);
        const today = new Date();
        
        if (isNaN(date.getTime()) || date >= today) {
          return "Date of birth must be a date in the past.";
        }
        
        // Calculate age BASED ON EVENT DATE
        const refDate = eventDate || new Date();
        let age = refDate.getFullYear() - date.getFullYear();
        const monthDiff = refDate.getMonth() - date.getMonth();
        if (
          monthDiff < 0 ||
          (monthDiff === 0 && refDate.getDate() < date.getDate())
        ) {
          age--;
        }

        // Check if youth-friendly category (10K, Kids Run, etc.)
        const catLower = (categoryName || "").toString().toLowerCase();
        const is10K =
          catLower.includes("10k") || catLower.includes("10 k") || catLower.includes("kids");

        // JMM Special Rule: Strictly 18+ for ALL categories
        if (eventId === "JMM") {
          if (age < 18) {
            return "Participant must be at least 18 years old to register for this event.";
          }
        } else if (catLower.includes("kids") && age > 17) {
          // Kaldera Kids Run is a children's race: 17 is the ceiling.
          return "Participant must be 17 years old or younger for this category.";
        } else if (eventId === "SMM" && (catLower.includes("7k") || catLower.includes("7 km") || catLower.includes("7km"))) {
          // SMM 7K: minimum 13 years; under-18 participants require consent.
          if (age < 13) {
            return "Participant must be at least 13 years old for this category.";
          }
        } else {
          // For other events: non-youth categories require minimum 18 years
          if (!is10K && age < 18) {
            return "Participant must be at least 18 years old for this category.";
          }
        }

        // Basic sanity check
        if (age < 5) {
          return "Please verify the date of birth is correct.";
        }
        break;
      }

      case "gender":
        if (!["Male", "Female", "Pria", "Wanita"].includes(value)) {
          return "Gender must be Male or Female.";
        }
        break;

      case "alamat": {
        const trimmedAlamat = String(value).trim();
        if (trimmedAlamat.length < 5 || trimmedAlamat.length > 250) {
          return "Address must be 5 - 250 characters.";
        }
        if (
          /[\x00\x01\x02\x03\x04\x05\x06\x07\x08\x0B\x0C\x0E-\x1F]/.test(value)
        ) {
          return "Address contains invalid characters.";
        }
        break;
      }

      case "namaKontakDarurat":
      case "hubunganKontakDarurat": {
        const trimmed = String(value).trim();
        if (trimmed.length > 50 || !/^[\p{L}\s'.-]{1,50}$/u.test(trimmed)) {
          return `${label} may only contain letters and spaces (max 50 chars).`;
        }
        break;
      }

      case "golonganDarah":
        if (!["A", "B", "AB", "O"].includes(value)) {
          return "Blood type must be one of A, B, AB, or O.";
        }
        break;

      case "sizeJersey":
        if (!["XS", "S", "M", "L", "XL", "XXL"].includes(value)) {
          return "Jersey size must be XS, S, M, L, XL, or XXL.";
        }
        break;

      default:
        break;
    }

    return "";
  };

  // === SUBMIT HANDLER ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSnapOpenRef.current) return;
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      // If user already registered, prevent duplicate registration attempts.
      if (registrationStatus === "registered") {
        if (transactionStatus === "pending" && snapToken) {
          throw new Error(
            "You are already registered and payment is pending. Use the 'Complete Payment' button to continue."
          );
        }
        throw new Error("You are already registered for this event.");
      }

      if (!isChecked) {
        throw new Error(
          "Please agree to the Declaration Letter before continuing."
        );
      }

      if (!categoryName) {
        throw new Error("Category data is incomplete.");
      }

      // validate required fields
      for (const field of [
        "namaDepan",
        "namaBelakang",
        "email",
        "telepon",
        "nomorIdentitas",
        "tanggalLahir",
        "gender",
        "negara",
        "provinsi",
        "kota",
        "alamat",
        "kontakDarurat",
        "namaKontakDarurat",
        "hubunganKontakDarurat",
      ]) {
        const error = validateFormat(field, formData[field]);
        if (error) {
          throw new Error(error);
        }
      }

      if (!Number.isInteger(finalPrice)) {
        throw new Error("Final price must be an integer.");
      }

      if (!eventId || !title) {
        throw new Error(
          "Event data is incomplete. Please retry from the category page."
        );
      }

      // === PARENT CONSENT VALIDATION FOR UNDER-18 IN YOUTH CATEGORIES ===
      const normalizedCategory = (categoryName || "").toString().toLowerCase();
      const is10KCategory =
        normalizedCategory.includes("10k") ||
        normalizedCategory.includes("10 k") ||
        normalizedCategory.includes("kids");
      const isSMM7KCategory =
        eventId === "SMM" &&
        (normalizedCategory.includes("7k") ||
          normalizedCategory.includes("7 km") ||
          normalizedCategory.includes("7km"));
      const requiresParentConsent = is10KCategory || isSMM7KCategory;
      
      // Calculate participant age
      let participantAge = null;
      if (formData.tanggalLahir) {
        const birthDate = new Date(formData.tanggalLahir);
        const referenceDate = eventDate || new Date();
        participantAge = referenceDate.getFullYear() - birthDate.getFullYear();
        const monthDiff = referenceDate.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
          participantAge--;
        }
      }
      
      const isUnder18 = participantAge !== null && participantAge < 18;
      let parentConsentUrl = null;
      
      if (requiresParentConsent && isUnder18) {
        if (!formData.parentConsentFile) {
          throw new Error(
            "Parent/Guardian Consent Form is required for participants under 18 years old. Please upload the signed consent form."
          );
        }
        
        // Upload parent consent file to Firebase Storage
        try {
          const file = formData.parentConsentFile;
          const timestamp = Date.now();
          const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
          const fileName = `${eventId}_${timestamp}_${sanitizedFilename}`;
          const filePath = `parentConsent/${user.uid}/${fileName}`;
          const storageRef = ref(storage, filePath);
          await uploadBytes(storageRef, file);
          parentConsentUrl = await getDownloadURL(storageRef);
          
          // Save to Firestore collection (same pattern as suratPernyataan)
          await addDoc(collection(db, "parentConsent"), {
            userId: user.uid,
            eventId,
            kategori: categoryName,
            fileName,
            downloadURL: parentConsentUrl,
            namaLengkap: formData.namaLengkap,
            tanggalLahir: formData.tanggalLahir,
            participantAge,
            createdAt: new Date(),
          });
        } catch (uploadErr) {
          console.error("Failed to upload parent consent file:", uploadErr);
          throw new Error("Failed to upload Parent Consent Form. Please try again.");
        }
      }

      const updatedUserData = {
        namaDepan: formData.namaDepan,
        namaBelakang: formData.namaBelakang,
        namaLengkap: formData.namaLengkap,
        telepon: formData.telepon,
        nomorIdentitas: formData.nomorIdentitas,
        tanggalLahir: formData.tanggalLahir,
        gender: formData.gender,
        negara: formData.negara,
        provinsi: formData.provinsi,
        kota: formData.kota,
        alamat: formData.alamat,
        kontakDarurat: formData.kontakDarurat,
        namaKontakDarurat: formData.namaKontakDarurat,
        hubunganKontakDarurat: formData.hubunganKontakDarurat,
        // persist profile-related fields (bib, blood type, jersey size)
        bibName: formData.bibName,
        golonganDarah: formData.golonganDarah,
        sizeJersey: formData.sizeJersey,
      };

      // update user profile document (safe set merge)
      await updateUserData(updatedUserData);

      // Determine declaration PDF language by participant country
      const langToSave = declarationLangForCountry(formData.negara);

      // save declaration PDF (in language based on country)
      await saveDeclarationAsPDF(langToSave);

      const payload = {
        namaDepan: formData.namaDepan,
        namaBelakang: formData.namaBelakang,
        namaLengkap: formData.namaLengkap,
        email: formData.email,
        telepon: formData.telepon,
        nomorIdentitas: formData.nomorIdentitas,
        tanggalLahir: formData.tanggalLahir,
        gender: formData.gender,
        negara: formData.negara,
        provinsi: formData.provinsi,
        kota: formData.kota,
        alamat: formData.alamat,
        kontakDarurat: formData.kontakDarurat,
        namaKontakDarurat: formData.namaKontakDarurat,
        hubunganKontakDarurat: formData.hubunganKontakDarurat,

        // profile fields
        sizeJersey: formData.sizeJersey || null,
        bibName: formData.bibName || null,
        golonganDarah: formData.golonganDarah || null,

        // Event & payment data
        kategori: categoryName,
        distance: distance || 0,
        harga: Math.floor(finalPrice),
        eventId,
        voucherCode: voucherCode || null,
        uid: user.uid,
        collectionName,
        paymentMethod,
        // Parent consent for under-18 participants
        parentConsentUrl: parentConsentUrl || null,
        participantAge: participantAge || null,
        // Age is judged as of race day on this side; send the reference date so the
        // server applies the same rule instead of measuring against "today".
        eventDate: eventDate ? eventDate.toISOString() : null,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/register`,
        payload
      );

      // set orderId early
      const returnedOrderId = response.data.orderId || response.data.order_id;
      setOrderId(returnedOrderId);

      const returnedTxStatus = response.data.transaction_status || null;
      if (returnedTxStatus) {
        setRegistrationStatus("registered");
        setTransactionStatus(returnedTxStatus);
      }

      // FREE registration
      if (finalPrice === 0) {
        setSuccessMsg("Free registration successful!");
        showNotification("success", "Free registration successful!");
        setTimeout(() => {
          navigate(
            `/MyOrders?order_id=${returnedOrderId}&transaction_status=settlement`
          );
        }, 1200);
        return;
      }

      // PayPal flow
      if (paymentMethod === "paypal") {
        // PayPal buttons will appear via useEffect watching orderId.
        return;
      }

      // Midtrans flow (snap)
      const { snapToken: returnedSnapToken, orderId: returnedSnapOrderId } =
        response.data;
      setSnapToken(returnedSnapToken);

      if (response.data.alreadyRegistered && returnedOrderId) {
        setSuccessMsg("Payment pending.");
        showNotification("warning", "Payment pending.");
        navigate(`/MyOrders?order_id=${returnedOrderId}&transaction_status=pending`);
        if (returnedSnapToken && window.snap?.pay && !isSnapOpenRef.current) {
          isSnapOpenRef.current = true;
          window.snap.pay(returnedSnapToken, {
            onSuccess: async () => {
              isSnapOpenRef.current = false;
              setSuccessMsg("Payment successful!");
              showNotification("success", "Payment successful!");
              navigate(`/MyOrders?order_id=${returnedOrderId}&transaction_status=settlement`);
            },
            onPending: async () => {
              isSnapOpenRef.current = false;
              setSuccessMsg("Payment is being processed.");
              showNotification("warning", "Payment is being processed.");
              navigate(`/MyOrders?order_id=${returnedOrderId}&transaction_status=pending`);
            },
            onError: () => {
              isSnapOpenRef.current = false;
              setErrorMsg("Payment failed. Please try again.");
              showNotification("error", "Payment failed. Please try again.");
              navigate(`/MyOrders?order_id=${returnedOrderId}&transaction_status=failed`);
            },
            onClose: async () => {
              isSnapOpenRef.current = false;
              try {
                if (collectionName) {
                  await checkRegistration(collectionName);
                }
              } catch (err) {
                if (collectionName) {
                  await checkRegistration(collectionName);
                }
              }
            },
          });
        }
        return;
      }

      isSnapOpenRef.current = true;
      window.snap.pay(returnedSnapToken, {
        onSuccess: async () => {
          isSnapOpenRef.current = false;
          setSuccessMsg("Payment successful!");
          showNotification("success", "Payment successful!");
          navigate(
            `/MyOrders?order_id=${returnedOrderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          isSnapOpenRef.current = false;
          setSuccessMsg("Payment is being processed.");
          showNotification("warning", "Payment is being processed.");
          navigate(
            `/MyOrders?order_id=${returnedOrderId}&transaction_status=pending`
          );
        },
        onError: () => {
          isSnapOpenRef.current = false;
          setErrorMsg("Payment failed. Please try again.");
          showNotification("error", "Payment failed. Please try again.");
          navigate(
            `/MyOrders?order_id=${returnedOrderId}&transaction_status=failed`
          );
        },
        onClose: async () => {
          isSnapOpenRef.current = false;
          try {
            // Note: /api/register/cancel endpoint doesn't exist in backend
            // Payment cancellation is handled by Midtrans itself
            // Just refresh registration status
            if (collectionName) {
              await checkRegistration(collectionName);
            }
          } catch (err) {
            if (collectionName) {
              await checkRegistration(collectionName);
            }
          }
        },
      });
    } catch (error) {
      // The backend returns per-field details in `errors` (see server/routes/payment.js
      // validateParticipant). Surface those instead of the generic message so the
      // participant knows which field to fix.
      const fieldErrors = error.response?.data?.errors;
      const fieldMessages =
        fieldErrors && typeof fieldErrors === "object"
          ? Object.values(fieldErrors).filter(Boolean)
          : [];

      const message = fieldMessages.length
        ? fieldMessages.join("\n")
        : error.response?.data?.message ||
          error.message ||
          "Registration failed. Please try again.";

      setErrorMsg(message);
      showNotification("error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleBayar = async () => {
    if (isSnapOpenRef.current) return;
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      if (!isSnapReady || !window.snap) {
        throw new Error("Payment gateway is not ready. Please try again.");
      }

      if (!snapToken || !orderId) {
        throw new Error("Payment token or order ID not found.");
      }

      isSnapOpenRef.current = true;
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          isSnapOpenRef.current = false;
          setSuccessMsg("Payment successful!");
          showNotification("success", "Payment successful!");
          navigate(
            `/MyOrders?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          isSnapOpenRef.current = false;
          setSuccessMsg("Payment is being processed.");
          showNotification("warning", "Payment is being processed.");
          navigate(`/MyOrders?order_id=${orderId}&transaction_status=pending`);
        },
        onError: () => {
          isSnapOpenRef.current = false;
          setErrorMsg("Payment failed. Please try again.");
          showNotification("error", "Payment failed. Please try again.");
          navigate(`/MyOrders?order_id=${orderId}&transaction_status=failed`);
        },
        onClose: async () => {
          isSnapOpenRef.current = false;
          if (collectionName) {
            await checkRegistration(collectionName);
          }
        },
      });
    } catch (err) {
      isSnapOpenRef.current = false;
      const message = err.message || "Failed to process payment.";
      setErrorMsg(message);
      showNotification("error", message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isSettled = transactionStatus === "settlement";
  const isPending = transactionStatus === "pending";

  // Compute explicit isForeigner for rendering PayPal option
  const countryNormalizedRender = (formData.negara || "")
    .toString()
    .trim()
    .toLowerCase();
  const isForeignerRender =
    countryNormalizedRender && countryNormalizedRender !== "indonesia";

  // isFormDisabled rules:
  // - disable when settled (already paid)
  // - disable when registered AND there's no snapToken to resume payment (prevents duplicate register)
  const isFormDisabled =
    isSettled || (registrationStatus === "registered" && !snapToken);

  return (
    <section className="w-full min-h-screen py-20 bg-white text-slate-700">
      <div className="container mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Main form area */}
          <div className="w-full md:w-3/4 max-w-4xl bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4">
            <h2 className="text-2xl font-semibold text-left mb-4 px-4 md:px-0">
              Registration Form
            </h2>
            <form
              id="form-pendaftaran"
              onSubmit={handleSubmit}
              className="px-4 md:px-0"
            >
              <InputFieldRace
                formData={formData}
                handleChange={handleChange}
                isFormDisabled={isFormDisabled}
                labelColor={labelColor}
                eventId={eventId}
                categoryName={categoryName}
                eventDate={eventDate}
                updateUserData={updateUserData}
              />
            </form>
          </div>

          {/* Sidebar order summary */}
          <aside className="w-full md:w-1/4 max-w-sm bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4 flex flex-col gap-4">
            <h2 className="text-2xl font-semibold text-left px-4 md:px-0">
              Order Summary
            </h2>

            <div className="text-lg text-left space-y-2 mt-2 px-4 md:px-0">
              <p className="text-sm text-gray-600">
                Event:
                <strong className="ml-1 text-slate-800">
                  {eventName || getEventDetails(eventId).namaEvent || eventId}
                </strong>
              </p>

              <p className="text-sm">
                Category: <strong>{categoryName || title}</strong>
              </p>

              <p className="text-sm">
                Price: <strong>Rp {finalPrice.toLocaleString("id-ID")}</strong>
              </p>

              {discount > 0 && (
                <p className="text-sm">
                  Discount: <strong>{discount}%</strong>
                </p>
              )}
            </div>

            {(errorMsg || successMsg) && (
              <div
                className={`p-3 rounded-md mt-2 mx-4 md:mx-0 ${
                  errorMsg
                    ? "bg-rose-50 text-rose-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {errorMsg || successMsg}
              </div>
            )}

            {registrationStatus === "registered" && isSettled && (
              <p className="text-left text-slate-800 font-medium mt-2 px-4 md:px-0">
                You are already registered for this event.
              </p>
            )}

            {registrationStatus === "registered" && isPending && (
              <p className="text-left text-slate-800 font-medium mt-2 px-4 md:px-0">
                Payment pending.
              </p>
            )}

            <div className="px-4 md:px-0 w-full">
              <hr className="border-t border-gray-200 my-3" />

              <VoucherInput
                onApplyVoucher={handleApplyVoucher}
                disabled={isFormDisabled}
                eventId={eventId}
                kategori={categoryName}
              />

              <div className="flex items-start gap-2 mt-2">
                <input
                  type="checkbox"
                  id="syarat"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  className="w-5 h-5 focus:ring-2 focus:ring-cyan-600"
                  disabled={isFormDisabled}
                />
                <label htmlFor="syarat" className="text-sm">
                  I have read and agree to the{" "}
                  <a
                    href={`/SuratPernyataan/${eventId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-cyan-600 underline"
                  >
                    Declaration Letter
                  </a>
                </label>
              </div>

              <div className="mt-4">
                {registrationStatus === "registered" && isSettled ? (
                  <Button fullWidth disabled>
                    Already Registered
                  </Button>
                ) : registrationStatus === "registered" && isPending ? (
                  snapToken ? (
                    <Button
                      fullWidth
                      onClick={handleBayar}
                      disabled={loading || !isChecked}
                    >
                      {loading ? "Processing..." : "Complete Payment"}
                    </Button>
                  ) : (
                    <Button fullWidth disabled>
                      Registration Pending
                    </Button>
                  )
                ) : (
                  <Button
                    type="submit"
                    form="form-pendaftaran"
                    fullWidth
                    disabled={loading || !isChecked}
                  >
                    {loading
                      ? "Processing..."
                      : finalPrice === 0
                      ? "Register for Free"
                      : paymentMethod === "paypal"
                      ? "Pay with PayPal"
                      : "Register & Pay"}
                  </Button>
                )}
              </div>

              {/* PayPal option for foreigners (UI is English but option shown based on country) */}
              {isForeignerRender && finalPrice > 0 && !isSettled && (
                <div className="mt-3">
                  <div className="flex gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentMethod("midtrans");
                      }}
                      disabled={!isChecked}
                      className={`flex-1 py-2 rounded-xl border ${
                        paymentMethod === "midtrans"
                          ? "bg-slate-700 text-white"
                          : ""
                      } ${!isChecked ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      Bank
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentMethod("paypal");
                      }}
                      disabled={isPending || !isChecked}
                      className={`flex-1 py-2 rounded-xl border ${
                        paymentMethod === "paypal"
                          ? "bg-yellow-500 text-white"
                          : ""
                      } ${
                        isPending || !isChecked
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      PayPal
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Amount: Rp {finalPrice.toLocaleString("id-ID")}. If you
                    choose PayPal, the PayPal flow will open after you submit
                    and the PayPal button will appear for you to complete the
                    payment.
                  </p>

                  {paymentMethod === "paypal" &&
                    orderId &&
                    finalPrice > 0 &&
                    !isSettled && (
                      <div className="mt-4 border-t pt-4">
                        <div ref={paypalRef} className="relative z-0"></div>
                      </div>
                    )}

                  {paymentMethod === "midtrans" && (
                    <p className="text-xs text-gray-500 mt-2">
                      After submitting, a payment window will open. If you
                      already have an ongoing transaction, choose “Complete
                      Payment”.
                    </p>
                  )}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default FormPendaftaran;
